import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import LessonEditor from './LessonEditorNew';
import BulkImport from './BulkImport';
import AdminStudySessions from './AdminStudySessions';
import AdminFlashcards from './AdminFlashcards';
import AdminDatabaseTools from './AdminDatabaseTools';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'sessions', 'flashcards'
  const [sessionsSubTab, setSessionsSubTab] = useState('sessions'); // 'sessions', 'lessons'
  const [lessons, setLessons] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [flashcardSets, setFlashcardSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showNewLesson, setShowNewLesson] = useState(false);
  const [duplicatingLesson, setDuplicatingLesson] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    await Promise.all([
      fetchLessons(),
      fetchSessions(),
      fetchFlashcardSets()
    ]);
    setLoading(false);
  };

  const fetchLessons = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/lessons', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLessons(response.data.lessons);
    } catch (error) {
      if (error.response?.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        onLogout();
      }
    }
  };

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/study-sessions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const fetchFlashcardSets = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/flashcards/sets', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFlashcardSets(response.data.sets || []);
    } catch (error) {
      console.error('Failed to fetch flashcard sets:', error);
    }
  };

  const handleDeleteLesson = async (lesson_id) => {
    if (!window.confirm(`Are you sure you want to delete ${lesson_id}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/lessons/${lesson_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast({
        title: "Success",
        description: `Lesson ${lesson_id} deleted successfully`
      });
      
      fetchLessons();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete lesson",
        variant: "destructive"
      });
    }
  };

  const handleDuplicateLesson = (lesson) => {
    // Create a copy of the lesson with a new ID
    const lessonCopy = {
      ...lesson,
      lesson_id: '', // Will need to set new ID
      title: `${lesson.title} (Copy)`,
      _id: undefined // Remove MongoDB ID
    };
    
    setDuplicatingLesson(lessonCopy);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    onLogout();
  };

  if (selectedLesson || showNewLesson) {
    return (
      <LessonEditor
        lesson={selectedLesson}
        onBack={() => {
          setSelectedLesson(null);
          setShowNewLesson(false);
          fetchLessons();
        }}
        isNew={showNewLesson}
      />
    );
  }

  if (duplicatingLesson) {
    return (
      <LessonEditor
        lesson={duplicatingLesson}
        onBack={() => {
          setDuplicatingLesson(null);
          fetchLessons();
        }}
        isNew={true}
        isDuplicate={true}
      />
    );
  }

  if (showBulkImport) {
    return (
      <BulkImport
        onBack={() => {
          setShowBulkImport(false);
          fetchLessons();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage content and settings</p>
          </div>
          <Button onClick={handleLogout} variant="outline">
            Logout
          </Button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b-2 border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'overview'
                ? 'border-b-4 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'sessions'
                ? 'border-b-4 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Manage Study Sessions
          </button>
          <button
            onClick={() => setActiveTab('flashcards')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'flashcards'
                ? 'border-b-4 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Manage Flashcards
          </button>
          <button
            onClick={() => setActiveTab('database')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'database'
                ? 'border-b-4 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Database Tools
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Stats Cards */}
            <div className="grid gap-4 mb-6 md:grid-cols-3">
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Sessions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{sessions.length}</div>
                  <p className="text-xs text-gray-500 mt-1">Study session collections</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Lessons</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{lessons.length}</div>
                  <p className="text-xs text-gray-500 mt-1">Individual lesson content</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Flashcard Sets</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">{flashcardSets.length}</div>
                  <p className="text-xs text-gray-500 mt-1">Flashcard collections</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common administrative tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <Button onClick={() => setActiveTab('sessions')} variant="outline" className="h-20">
                    <div className="text-center">
                      <div className="text-lg font-semibold">📚</div>
                      <div className="text-sm">Manage Sessions</div>
                    </div>
                  </Button>
                  <Button onClick={() => setActiveTab('flashcards')} variant="outline" className="h-20">
                    <div className="text-center">
                      <div className="text-lg font-semibold">🎴</div>
                      <div className="text-sm">Manage Flashcards</div>
                    </div>
                  </Button>
                  <Button onClick={() => {
                    setActiveTab('sessions');
                    setSessionsSubTab('lessons');
                  }} variant="outline" className="h-20">
                    <div className="text-center">
                      <div className="text-lg font-semibold">📖</div>
                      <div className="text-sm">View All Lessons</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Manage Study Sessions Tab */}
        {activeTab === 'sessions' && (
          <div>
            {/* Sub-tabs */}
            <div className="flex gap-2 mb-4 bg-white p-2 rounded-lg">
              <button
                onClick={() => setSessionsSubTab('sessions')}
                className={`px-4 py-2 rounded font-medium transition-all ${
                  sessionsSubTab === 'sessions'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Study Sessions
              </button>
              <button
                onClick={() => setSessionsSubTab('lessons')}
                className={`px-4 py-2 rounded font-medium transition-all ${
                  sessionsSubTab === 'lessons'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Lessons
              </button>
            </div>

            {sessionsSubTab === 'sessions' ? (
              <AdminStudySessions onBack={() => setActiveTab('overview')} />
            ) : (
              // All Lessons View
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle>All Lessons</CardTitle>
                      <CardDescription>View and manage individual lesson content</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => setShowNewLesson(true)}>
                        + New Lesson
                      </Button>
                      <Button onClick={() => setShowBulkImport(true)} variant="outline">
                        Bulk Import
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p>Loading lessons...</p>
                  ) : (
                    <div className="space-y-2">
                      {lessons.map((lesson) => (
                        <div
                          key={lesson.lesson_id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <h3 className="font-semibold">{lesson.title}</h3>
                            <p className="text-sm text-gray-600">{lesson.lesson_id} - {lesson.verses}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Scripture: {lesson.scripture?.length || 0} | 
                              Activities: {lesson.activities?.length || 0} | 
                              Trivia: {lesson.trivia?.length || 0}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => setSelectedLesson(lesson)}
                              variant="outline"
                              size="sm"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => setDuplicatingLesson(lesson)}
                              variant="outline"
                              size="sm"
                            >
                              Duplicate
                            </Button>
                            <Button
                              onClick={() => handleDeleteLesson(lesson.lesson_id)}
                              variant="destructive"
                              size="sm"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Manage Flashcards Tab */}
        {activeTab === 'flashcards' && (
          <AdminFlashcards />
        )}

        {/* Database Tools Tab */}
        {activeTab === 'database' && (
          <AdminDatabaseTools />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
